/**
 * ui#78 mitigation: on warm-cache loads, vite-react-ssg's `startTransition(() => hydrateRoot(...))`
 * can be interrupted; React 19's recovery then leaves the pre-rendered STATIC tree in `#root` and
 * APPENDS the freshly client-rendered tree after it. The user sees the stale shell above the live
 * app — on `/download/<id>` that's the skeleton stuck on "Getting your files…", on other routes a
 * duplicated page/nav. That stale APP tree is inert markup (no JS owns it), so dropping it is safe.
 *
 * RootLayout renders exactly ONE app-tree DOM node (the `display: contents` app-root wrapper) and
 * calls this after mount with that node. Every OTHER app-tree child of the container is, by
 * construction, a leftover from the abandoned hydration pass.
 *
 * ui#150 correction — do NOT sweep React-owned hoistable document metadata. Current production SSG
 * places `<Seo>` metadata in `<head>`, but this helper exists specifically for an interrupted hydration:
 * if a partial or older tree leaves a `<title>`, `<meta>`, `<link>` or ld+json `<script>` beside the app
 * root, detaching it can still violate React's ownership. The original blanket sweep did exactly that;
 * the next `<Seo>` unmount then ran `parentNode.removeChild(node)` with a null parent and crashed (the
 * failure surfaced by reverted commit 72598f7 and by logged-in "/"→feature navigation). Skipping
 * hoistable tags while removing only stale app-tree siblings keeps the recovery path ownership-safe and
 * prevents metadata nodes from masking the genuine ui#78 warning count.
 */

/** React-19 hoistable document-metadata tags — React owns these; the sweeper must never detach them. */
const HOISTABLE_METADATA_TAGS: ReadonlySet<string> = new Set(["TITLE", "META", "LINK", "SCRIPT", "STYLE", "BASE"]);

export function removeStaleHydrationSiblings(container: ParentNode | null, liveNode: Element | null): number {
	if (!container || !liveNode) {
		return 0;
	}
	let removed = 0;
	for (const child of [...container.children]) {
		if (child === liveNode || HOISTABLE_METADATA_TAGS.has(child.tagName)) {
			continue;
		}
		child.remove();
		removed++;
	}
	return removed;
}
